import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useLanguage } from '../../contexts/LanguageContext';

export default function ContactModal({ onClose, onSave, initialData }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState(
    initialData || { firstName: '', lastName: '', phone: '', email: '', notes: '' }
  );
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    // First Name validation
    if (!formData.firstName || !formData.firstName.trim()) {
      newErrors.firstName = t('firstNameRequired') || 'First Name is required';
    }

    // Phone validation
    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = t('phoneRequired') || 'Phone number is required';
    } else {
      const digits = formData.phone.replace(/\D/g, '');
      if (digits.length < 7) {
        newErrors.phone = t('invalidPhone') || 'Invalid Phone Number.';
      }
    }

    // Email validation (optional, but must be valid if entered)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = t('invalidEmail') || 'Invalid email address format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field-specific error as user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (validate()) {
      setIsSubmitting(true);
      setApiError('');
      try {
        const phoneToSave = formData.phone.startsWith('+') ? formData.phone : `+${formData.phone.replace(/\D/g, '')}`;
        await onSave({ ...formData, phone: phoneToSave });
      } catch (err) {
        setApiError(err.message || 'Failed to create contact. It may already exist.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-scale-in flex flex-col">
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center rounded-t-2xl">
          <h3 className="font-bold text-gray-800">
            {initialData ? t('editContact') : t('addNewContact')}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {apiError && (
          <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl animate-fade-in">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{apiError}</span>
            <button onClick={() => setApiError('')} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('firstName')} <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                className={`w-full p-2 border rounded-lg mt-1 outline-none transition-all focus:ring-2 ${
                  errors.firstName
                    ? 'border-red-400 bg-red-50/30 focus:ring-red-200 focus:border-red-500'
                    : 'border-gray-200 focus:ring-blue-100 focus:border-blue-500'
                }`}
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Ali"
              />
              {errors.firstName && (
                <p className="text-red-500 text-[11px] font-semibold mt-1 animate-fade-in">{errors.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">{t('lastName')}</label>
              <input
                className="w-full p-2 border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Khan"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase">
              {t('phone')} <span className="text-red-500 ml-0.5">*</span>
            </label>
            <PhoneInput
              country={'pk'}
              value={formData.phone}
              onChange={(phone) => handleInputChange('phone', phone)}
              containerClass="mt-1"
              inputClass={`!w-full !p-2 !pl-[48px] !border !rounded-lg !outline-none !transition-all focus:!ring-2 ${
                errors.phone
                  ? '!border-red-400 !bg-red-50/30 focus:!ring-red-200 focus:!border-red-500'
                  : '!border-gray-200 focus:!ring-blue-100 focus:!border-blue-500'
              }`}
              buttonClass={`!border-gray-200 !bg-gray-50 !rounded-l-lg ${errors.phone ? '!border-red-400 !bg-red-50/30' : ''}`}
              dropdownClass="!rounded-lg !shadow-2xl !border-gray-200 !max-h-[140px] !overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
            />
            {errors.phone && (
              <p className="text-red-500 text-[11px] font-semibold mt-1 animate-fade-in">{errors.phone}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase">{t('emailOptional')}</label>
            <input
              className={`w-full p-2 border rounded-lg mt-1 outline-none transition-all focus:ring-2 ${
                errors.email
                  ? 'border-red-400 bg-red-50/30 focus:ring-red-200 focus:border-red-500'
                  : 'border-gray-200 focus:ring-blue-100 focus:border-blue-500'
              }`}
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="ali@example.com"
            />
            {errors.email && (
              <p className="text-red-500 text-[11px] font-semibold mt-1 animate-fade-in">{errors.email}</p>
            )}
          </div>
        </div>
        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-6 py-2 rounded-lg font-bold transition-colors shadow-sm ${
              isSubmitting ? 'bg-blue-400 text-white cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isSubmitting ? t('saving') || 'Saving...' : t('saveContact')}
          </button>
        </div>
      </div>
    </div>
  );
}
